import styled from 'styled-components';

export const Nav = styled.div`
  background-color: ${({ theme }) => theme.bg};
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  position: sticky;
  top: env(safe-area-inset-top);
  z-index: var(--z-navbar);
  border-bottom: 2px solid ${({ theme }) => theme.primary};
  @media (max-width: 960px) {
    transition: 0.8s all ease;
  }
`;

export const ProgressBar = styled.div`
  position: fixed;
  top: 0;
  ${({ $language }) => ($language === 'ar' ? 'right: 0' : 'left: 0')};
  width: ${({ progress }) => progress}%;
  height: 4px;
  background-color: ${({ theme }) => theme.primary};
  z-index: var(--z-dropdown);
  transition: width 0.1s ease-out;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
  will-change: transform;
`;

export const ThemeToggleWrapper = styled.div`
  display: none;
  @media screen and (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 20px;
    right: ${({ $language }) => ($language === 'ar' ? 'auto' : '72px')};
    left: ${({ $language }) => ($language === 'ar' ? '72px' : 'auto')};
    font-size: 1.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.text_primary};
    height: 40px;
    width: 40px;

    /* Jitter prevention */
    box-sizing: border-box;
    transform: translateZ(0);
    -webkit-transform: translateZ(0);
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    perspective: 1000px;
    -webkit-perspective: 1000px;
    touch-action: manipulation;
    -webkit-tap-highlight-color: transparent;
    will-change: transform;

    /* Icon container */
    > * {
      display: flex;
      align-items: center;
      justify-content: center;
      transform: translateZ(0);
    }

    &:active {
      transform: translateZ(0) scale(0.95);
      transition: transform 0.1s ease;
    }
  }
`;

export const NavbarContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 60px;
  z-index: var(--z-content);
  width: 100%;
  padding: 0 24px;
  padding-top: env(safe-area-inset-top);
  max-width: 1200px;
`;

export const NavLogo = styled.div`
  width: auto;
  padding: 0 6px;
  display: flex;
  justify-content: start;
  align-items: center;
  text-decoration: none;
  height: 40px;
  @media (max-width: 640px) {
    padding: 0 0px;
  }
`;

export const Span = styled.div`
  padding: 0 4px;
  font-weight: bold;
  font-size: 16.5px;
  color: ${({ theme }) => theme.text_primary};
  display: flex;
  align-items: center;
`;

export const NavItems = styled.ul`
  width: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 32px;
  padding: 0 6px;
  list-style: none;

  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

export const NavLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 2px;
    bottom: -4px;
    ${({ $language }) => ($language === 'ar' ? 'right: 0' : 'left: 0')};
    background-color: ${({ theme }) => theme.primary};
    transition: width 0.3s ease;
    ${({ $noUnderline }) => $noUnderline && 'display: none;'};
  }

  &:hover {
    color: ${({ theme, $noUnderline }) =>
      $noUnderline ? 'inherit' : theme.primary};
  }

  &.active {
    border-bottom: 2px solid ${({ theme }) => theme.primary};
  }

  &:hover::after {
    width: ${({ $noUnderline }) => ($noUnderline ? '0' : '100%')};
  }
`;

export const GitHubButton = styled.div`
  border: 1.8px solid ${({ theme }) => theme.primary};
  justify-content: center;
  display: flex;
  align-items: center;
  height: 40px;
  min-height: 40px;
  width: 40px;
  border-radius: 15px;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  padding: 0;
  font-weight: 500;
  text-decoration: none;
  font-size: 16px;
  transition: all 0.4s ease-in-out;

  a {
    color: ${({ theme }) => theme.text_primary};
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.white};
    border: 1.8px solid ${({ theme }) => theme.primary};

    a {
      color: ${({ theme }) => theme.white};
    }
  }

  @media screen and (max-width: 1024px) {
    font-size: 14px;
  }
`;

export const ThemeButton = styled.div`
  border: 1.8px solid ${({ theme }) => theme.primary};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  min-height: 40px;
  width: 40px;
  border-radius: 15px;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  padding: 0;
  font-weight: 500;
  text-decoration: none;
  font-size: 16px;
  transition:
    background-color 0.4s ease-in-out,
    color 0.4s ease-in-out,
    border-color 0.4s ease-in-out;

  /* Jitter prevention */
  box-sizing: border-box;
  transform: translateZ(0);
  -webkit-transform: translateZ(0);
  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
  perspective: 1000px;
  -webkit-perspective: 1000px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  will-change: transform;

  /* Icon container */
  > * {
    display: flex;
    align-items: center;
    justify-content: center;
    transform: translateZ(0);
  }

  &:hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.white};
    border: 1.8px solid ${({ theme }) => theme.primary};
  }

  &:active {
    transform: translateZ(0) scale(0.95);
    transition: transform 0.1s ease;
  }

  @media screen and (max-width: 1024px) {
    font-size: 14px;
  }
`;

export const ButtonContainer = styled.div`
  width: auto;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
  padding: 0 6px;
  @media screen and (max-width: 1024px) {
    display: none;
  }
`;

export const MobileIcon = styled.div`
  display: none;
  @media screen and (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 20px;
    right: ${({ $language }) => ($language === 'ar' ? 'auto' : '24px')};
    left: ${({ $language }) => ($language === 'ar' ? '24px' : 'auto')};
    font-size: 1.5rem;
    cursor: pointer;
    color: ${({ theme }) => theme.text_primary};
    height: 40px;
    width: 40px;
  }
`;

export const MobileMenu = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  position: absolute;
  top: 80px;
  right: ${({ $language }) => ($language === 'ar' ? 'auto' : '0')};
  left: ${({ $language }) => ($language === 'ar' ? '0' : 'auto')};
  width: 100%;
  padding: 12px 40px 12px 40px;
  background: ${({ theme }) => theme.bg};
  border-top: 2px solid ${({ theme }) => theme.primary};
  border-bottom: 2px solid ${({ theme }) => theme.primary};
  transition: opacity 0.2s ease-out;
  transform: translateY(0);
  opacity: ${({ isOpen }) => (isOpen ? '1' : '0')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
`;

export const MobileMenuItems = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 32px;
  list-style: none;
  width: 100%;
  height: 100%;
`;

export const MobileMenuButton = styled.a`
  border: 1.8px solid ${({ theme }) => theme.primary};
  justify-content: center;
  display: flex;
  align-items: center;
  height: 70%;
  border-radius: 20px;
  color: ${({ theme }) => theme.primary};
  cursor: pointer;
  padding: 0 20px;
  font-weight: 500;
  text-decoration: none;
  font-size: 16px;
  transition: all 0.6s ease-in-out;

  :hover {
    background: ${({ theme }) => theme.primary};
    color: ${({ theme }) => theme.white};
  }
`;

export const MobileLink = styled.a`
  color: ${({ theme }) => theme.text_primary};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  text-decoration: none;
  @media (hover: none) and (pointer: coarse) {
    :hover {
      color: ${({ theme }) => theme.text_primary};
    }

    &.active {
      border-bottom: none;
    }
  }
  @media (hover: hover) and (pointer: fine) {
    :hover {
      color: ${({ theme }) => theme.primary};
    }

    &.active {
      border-bottom: 2px solid ${({ theme }) => theme.primary};
    }
  }
`;

export const LanguageDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

export const LanguageButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  min-height: 40px;
  width: 40px;
  border-radius: 15px;
  color: ${({ theme }) => theme.text_primary};
  cursor: pointer;
  border: 1.8px solid ${({ theme }) => theme.primary};
  font-weight: 500;
  transition: all 0.4s ease-in-out;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;

  img {
    width: 19px;
    height: 19px;
    object-fit: contain;
  }

  &:hover {
    background: ${({ theme }) => theme.primary};
  }
  @media screen and (max-width: 1024px) {
    font-size: 14px;
  }
`;

export const LanguageOption = styled.div`
  display: flex;
  align-items: center;
  padding: 10px 20px;
  cursor: pointer;
  color: white;
  font-weight: 500;
  background: ${({ theme }) => theme.primary};
  border: 1.8px solid ${({ theme }) => theme.primary};
  border-radius: 15px;
  transition: all 0.3s ease-in-out;
  user-select: none !important;
  -webkit-user-select: none !important;
  -moz-user-select: none !important;
  -ms-user-select: none !important;

  position: absolute;
  top: -10px;
  left: 0;
  z-index: var(--z-tooltip);

  &:hover {
    color: ${({ theme }) => theme.white};
    border: 1.8px solid white;
  }
`;

export const MobileLanguageButton = styled.div`
  display: none;

  @media screen and (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: absolute;
    top: 20px;
    right: ${({ $language }) => ($language === 'ar' ? 'auto' : '120px')};
    left: ${({ $language }) => ($language === 'ar' ? '120px' : 'auto')};
    font-size: 1.5rem;
    border-radius: 50%;
    overflow: hidden;
    cursor: pointer;
    z-index: var(--z-button);
    height: 40px;
    width: 40px;

    &:hover {
      background: none;
      color: inherit;
    }

    img {
      width: 20px;
      height: 20px;
      object-fit: cover;
    }
  }
`;
